import { t } from "../../i18n";
import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { CadShell } from "./CadShell";
import { Palette } from "./Palette";

export interface DocLink { to: string; label: string }
/** Belge listesi (README §4.6): gizlilik, kullanım şartları, ön bilgilendirme, mesafeli satış + kılavuz */
export const DOC_LINKS: DocLink[] = [
  { to: "/kilavuz", label: t("Kullanım kılavuzu") }, { to: "/gizlilik", label: t("Gizlilik bildirimi (KVKK)") }, { to: "/kullanim-sartlari", label: t("Kullanım şartları") },
  { to: "/on-bilgilendirme", label: t("Ön Bilgilendirme Formu") }, { to: "/mesafeli-satis", label: t("Mesafeli Satış Sözleşmesi") },
];

/**
 * Belge paftası (README §4.5–4.6): sol 268 px BELGELER paleti (seçili satır #E2E8F0 + 2 px sol kenar; isteğe bağlı bölüm listesi) + Yazdır;
 * rapor paftasıyla aynı koyu masa üstünde beyaz kâğıt (`padding 22px`, iç kesikli baskı sınırı), altta antet tablosu (belge adı · sürüm/tarih).
 * Kâğıt tipografisi kâğıt token'larıyla (12 px, #16233B, #7C8794, #D8D6CF) — kabuk yoğunluğu uygulanmaz.
 */
export function DocSheet({ title, children, sections, version, width = 680, lang }: { title: string; children: ReactNode; sections?: { id: string; label: string }[]; version?: string; width?: number; lang?: string }): JSX.Element {
  const { pathname } = useLocation();
  return (
    <CadShell current="start">
      <Palette as="aside" title={t("Belgeler")} className="no-print shrink-0 border-r border-cad-line" style={{ width: 268 }}
        footer={<button type="button" className="cad-btn-box" onClick={() => window.print()}>{t("Yazdır")}</button>}>
        <ul className="m-0 p-1.5 list-none grid gap-px" aria-label={t("Belgeler")}>
          {DOC_LINKS.map((d) => <li key={d.to}><Link to={d.to} aria-current={pathname === d.to ? "page" : undefined} className="block px-2 py-1 text-[12px] text-cad-ink2 rounded-[2px] hover:bg-cad-hover aria-[current=page]:bg-cad-select aria-[current=page]:text-cad-accent aria-[current=page]:font-medium aria-[current=page]:border-l-2 aria-[current=page]:border-cad-accent">{d.label}</Link></li>)}
        </ul>
        {sections ? <>
          <div className="cad-head">{t("Bölümler")}</div>
          <ol className="m-0 p-1.5 list-none grid gap-px" aria-label={t("Bölümler")}>
            {sections.map((s, i) => <li key={s.id}><a href={`#${s.id}`} className="block px-2 py-0.5 text-[11px] text-cad-ink2 rounded-[2px] hover:bg-cad-hover"><span className="font-mono text-cad-ink3 mr-1">{i + 1}.</span>{s.label}</a></li>)}
          </ol>
        </> : null}
      </Palette>
      <div className="cad-desk flex-1 overflow-auto bg-[#3A3F45] p-5 min-w-0">
        <article className="doc-paper mx-auto bg-white text-[#16233B] shadow-[0_0_0_1px_#24272B,0_14px_30px_-12px_rgb(0_0_0/0.6)] p-[22px] grid" style={{ width, maxWidth: "100%" }} lang={lang} aria-label={title}>
          <div className="border border-dashed border-cad-line p-[18px] grid gap-3 text-[12px] leading-[1.5]">
            <h1 className="text-[16px] font-semibold m-0 pb-2 border-b-2 border-[#16233B]">{title}</h1>
            {children}
            <table className="mt-auto w-full border border-[#16233B] border-collapse text-[10px]" aria-hidden>
              <tbody><tr><td className="p-1.5 border-r border-[#16233B]"><div className="text-[#7C8794]">{t("BELGE")}</div><div className="font-medium">{title}</div></td><td className="p-1.5 font-mono text-right text-[#7C8794]"><div>{version ? `${t("Sürüm")} ${version}` : "TaşDuvar"}</div><div>{new Date().toLocaleDateString("tr-TR")}</div></td></tr></tbody>
            </table>
          </div>
        </article>
      </div>
    </CadShell>
  );
}
